/**
 * useFirstTouchDrafts — the bulk wizard's drafting call, and the list of Bulk sends
 * not yet sent behind the header title. Drafting is a durable operation: the
 * wizard polls it by id (useOutreachOperation); the list is how a closed or
 * reloaded wizard, or another admin, finds the sends still going or waiting.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FirstTouchDraftsInput, OutreachBulkSend, OutreachFirstTouchDrafts } from '@discovr/types';
import { campaignKeys } from '../queryKeys';
import { fetchFirstTouchDrafts, fetchOutreachBulkSends, fetchOutreachDrafts, selectFirstTouchDraft, startFirstTouchDrafts } from '../api/outreachApi';

export const BULK_SENDS_POLL_MS = 5000;
/** Sends listed but none running: a send another admin starts shows up within this. An empty list stops polling; the draft events refresh it. */
export const BULK_SENDS_IDLE_POLL_MS = 30000;
/** The drafts route caps a page at 100. */
const FLOOR_PAGE_LIMIT = 100;

export interface StartedFirstTouchDrafts {
  operationId: string;
  /** The newest initial draft's message id just before the start (0 when none): a draft this operation writes has a larger id. */
  draftFloor: number;
}

export function useStartFirstTouchDrafts(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation<StartedFirstTouchDrafts, Error, FirstTouchDraftsInput>({
    // A fresh key per start: re-running a batch is a new operation, not a replay.
    mutationFn: async (input) => {
      // Go keeps the old draft until its successor is written, so the wizard needs to tell the two apart.
      // A page, not one row: rows of overlapping transactions can be out of id order.
      const newest = await fetchOutreachDrafts(campaignId, { kind: 'initial', page: 1, limit: FLOOR_PAGE_LIMIT });
      // Go drops rows whose roster entry is gone after paging, so an empty page is not an empty list.
      if (newest.items.length === 0 && newest.total > 0) throw new Error('Could not read the existing drafts. Try again.');
      const draftFloor = newest.items.reduce((max, item) => Math.max(max, item.message.id), 0);
      const { operationId } = await startFirstTouchDrafts(campaignId, input, crypto.randomUUID());
      return { operationId, draftFloor };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: campaignKeys.outreachBulkSends(campaignId) });
      void queryClient.invalidateQueries({ queryKey: campaignKeys.outreachDraftsAll(campaignId) });
    },
  });
}

export function bulkSendsRefetchInterval(sends: OutreachBulkSend[] | undefined): number | false {
  if (sends?.some((send) => send.running)) return BULK_SENDS_POLL_MS;
  return sends?.length === 0 ? false : BULK_SENDS_IDLE_POLL_MS;
}

/** The campaign's Bulk sends not yet sent (empty while loading or after a failed read). The outreach tab's draft
 *  events refresh it too; the poll covers a dropped stream and sends other admins start. */
export function useOutreachBulkSends(campaignId: string): OutreachBulkSend[] {
  const query = useQuery<OutreachBulkSend[]>({
    queryKey: campaignKeys.outreachBulkSends(campaignId),
    queryFn: () => fetchOutreachBulkSends(campaignId),
    enabled: !!campaignId,
    refetchInterval: (q) => bulkSendsRefetchInterval(q.state.data),
  });
  return query.isError ? EMPTY : (query.data ?? EMPTY);
}

const EMPTY: OutreachBulkSend[] = [];

/** A creator's first-touch drafts for the Drafts step's "Draft N of M" menu. Reading never writes a draft. */
export function useFirstTouchDraftList(entryId: string, enabled = true) {
  return useQuery<OutreachFirstTouchDrafts>({
    queryKey: campaignKeys.firstTouchDrafts(entryId),
    queryFn: () => fetchFirstTouchDrafts(entryId),
    enabled: enabled && !!entryId,
  });
}

/** Picks an earlier draft as the current one (the one Send uses); the lists, rows and Bulk sends refetch. */
export function useSelectFirstTouchDraft(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation<OutreachFirstTouchDrafts, Error, { entryId: string; messageId: number }>({
    mutationFn: (v) => selectFirstTouchDraft(v.entryId, v.messageId, crypto.randomUUID()),
    onSettled: (_o, _e, v) => {
      void queryClient.invalidateQueries({ queryKey: campaignKeys.firstTouchDrafts(v.entryId) });
      void queryClient.invalidateQueries({ queryKey: campaignKeys.outreachDraftsAll(campaignId) });
      void queryClient.invalidateQueries({ queryKey: campaignKeys.outreachBulkSends(campaignId) });
    },
  });
}
